import {
	DocumentStatus,
	EWorkerDocumentType,
	type IHabilitationDocument,
	type WorkerDocumentOption,
} from '@/types/documents';

import type { DocumentTypeRefApi } from '@/types/talentplug';

type LegacyDocumentTypeRef = DocumentTypeRefApi & {
	documentName?: string;
	idDocument?: string;
	ocrProvider?: string | null;
	ocrDocumentType?: string | null;
	ocrFieldMapping?: unknown;
};

/** Maps legacy API status values to the unified DocumentStatus enum. */
const LEGACY_DOCUMENT_STATUS_MAPPING: Record<string, DocumentStatus> = {
	PENDING: DocumentStatus.TO_VALIDATE,
	VALIDATED: DocumentStatus.VALID,
	TO_SEND: DocumentStatus.TO_VERIFY,
	LEGACY: DocumentStatus.REPRISE,
};

export const normalizeDocumentStatus = (
	status: string | null | undefined
): DocumentStatus => {
	if (!status) return DocumentStatus.TO_VALIDATE;

	const upperStatus = status.toUpperCase();
	if ((Object.values(DocumentStatus) as string[]).includes(upperStatus)) {
		return upperStatus as DocumentStatus;
	}

	return (
		LEGACY_DOCUMENT_STATUS_MAPPING[upperStatus] ?? DocumentStatus.TO_VALIDATE
	);
};

const normalizeDocumentTypeRefs = (
	refs:
		| DocumentTypeRefApi[]
		| Record<string, DocumentTypeRefApi[]>
		| null
		| undefined
): LegacyDocumentTypeRef[] => {
	if (!refs) return [];
	if (Array.isArray(refs)) return refs;
	return Object.values(refs).flat();
};

export const mapDocumentTypeRefsByCategory = (
	refs:
		| DocumentTypeRefApi[]
		| Record<string, DocumentTypeRefApi[]>
		| null
		| undefined
): Record<string, WorkerDocumentOption[]> => {
	return normalizeDocumentTypeRefs(refs)
		.map((document) => ({
			label: document.name || document.documentName || '',
			value: document.id || document.idDocument || '',
			type: document.type,
			category: document.category,
			duration: document.duration,
			fields: document.fields,
			acceptedFormats: document.acceptedFormats,
			maxSizeMb: document.maxSizeMb,
			ocrEnabled: document.ocrEnabled || false,
			ocrProvider: document.ocrProvider || null,
			ocrDocumentType: document.ocrDocumentType || null,
			ocrFieldMapping: document.ocrFieldMapping || null,
			idCheckhub: document.idCheckhub || null,
			evoliaCode: document.evoliaCode || null,
		}))
		.reduce<Record<string, WorkerDocumentOption[]>>((acc, curr) => {
			const key = curr.category || curr.type;
			if (!acc[key]) acc[key] = [];
			acc[key].push(curr);
			return acc;
		}, {});
};

export const mapHabilitationDocumentOptions = (
	refs: DocumentTypeRefApi[]
): IHabilitationDocument[] =>
	refs
		.filter((docRef) => docRef.type === EWorkerDocumentType.HABILITATION)
		.map((docRef) => ({
			label: docRef.name,
			value: docRef.id,
			type: docRef.type,
			duration: docRef.duration ?? 0,
		}));
